import { SortableElement, SortableHandle } from 'react-sortable-hoc';

import { useCallback, useState } from 'react';
import clsx from 'clsx';
import { CartSiteAddress } from 'screens/AccessExpressV3/CartProvider';
import { SearchAccessAddressBar } from './SearchAccessAddressBar';
import { QuoteLocation } from 'api/accessexpress/schema';

interface LocationItemProps {
  value: CartSiteAddress;
  onChange: (index: number, value?: CartSiteAddress) => void;
  index_: number;
}

const LocationItem = SortableElement<LocationItemProps>(
  ({ value, onChange, index_ }: LocationItemProps) => {
    const handleChange = useCallback(
      (value?: QuoteLocation) =>
        onChange(index_, value ? { ...value, id: `${value?.id}` } : undefined),
      [index_, onChange],
    );
    const [isDragging, setDragging] = useState(false);

    return (
      <div
        className={clsx('relative mb-8 flex flex-none items-center gap-1', {
          'text-teal-400 opacity-25 shadow-dragged': isDragging,
          'text-white': !isDragging,
        })}
      >
        <DragHandle setDragging={setDragging} isDragging={isDragging} />
        <div className="flex flex-1">
          <SearchAccessAddressBar
            onAddressSelected={handleChange}
            className="flex flex-grow [&_.search-bar-label]:w-[274px] [&_.search-bar-label]:py-0.5 [&_.search-bar-label]:text-sm"
            value={value as QuoteLocation}
            prefixElement={
              <div className="-ml-2 flex w-4 flex-none justify-end text-sm text-content-base-subdued">
                {index_ + 1}.
              </div>
            }
          />
        </div>
      </div>
    );
  },
);

export default LocationItem;

interface DragHandleProps {
  setDragging?: (v: boolean) => void;
  isDragging?: boolean;
}

export const DragHandle = SortableHandle<DragHandleProps>(
  ({ setDragging }: DragHandleProps) => {
    return (
      <div
        className="flex h-[48px] w-6 flex-none cursor-row-resize items-center justify-center"
        onMouseDownCapture={() => setDragging?.(true)}
        onMouseUpCapture={() => setDragging?.(false)}
        onMouseLeave={() => setDragging?.(false)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="18"
          viewBox="0 0 8 18"
          fill="none"
        >
          <path
            d="M1.5 3C1.10218 3 0.720644 2.84196 0.43934 2.56066C0.158035 2.27936 0 1.89782 0 1.5C0 1.10218 0.158035 0.720644 0.43934 0.43934C0.720644 0.158035 1.10218 0 1.5 0C1.89782 0 2.27936 0.158035 2.56066 0.43934C2.84196 0.720644 3 1.10218 3 1.5C3 1.89782 2.84196 2.27936 2.56066 2.56066C2.27936 2.84196 1.89782 3 1.5 3ZM1.5 8C1.10218 8 0.720644 7.84196 0.43934 7.56066C0.158035 7.27936 0 6.89782 0 6.5C0 6.10218 0.158035 5.72064 0.43934 5.43934C0.720644 5.15804 1.10218 5 1.5 5C1.89782 5 2.27936 5.15804 2.56066 5.43934C2.84196 5.72064 3 6.10218 3 6.5C3 6.89782 2.84196 7.27936 2.56066 7.56066C2.27936 7.84196 1.89782 8 1.5 8ZM1.5 13C1.10218 13 0.720644 12.842 0.43934 12.5607C0.158035 12.2794 0 11.8978 0 11.5C0 11.1022 0.158035 10.7206 0.43934 10.4393C0.720644 10.158 1.10218 10 1.5 10C1.89782 10 2.27936 10.158 2.56066 10.4393C2.84196 10.7206 3 11.1022 3 11.5C3 11.8978 2.84196 12.2794 2.56066 12.5607C2.27936 12.842 1.89782 13 1.5 13ZM1.5 18C1.10218 18 0.720644 17.842 0.43934 17.5607C0.158035 17.2794 0 16.8978 0 16.5C0 16.1022 0.158035 15.7206 0.43934 15.4393C0.720644 15.158 1.10218 15 1.5 15C1.89782 15 2.27936 15.158 2.56066 15.4393C2.84196 15.7206 3 16.1022 3 16.5C3 16.8978 2.84196 17.2794 2.56066 17.5607C2.27936 17.842 1.89782 18 1.5 18ZM6.5 3C6.10218 3 5.72064 2.84196 5.43934 2.56066C5.15804 2.27936 5 1.89782 5 1.5C5 1.10218 5.15804 0.720644 5.43934 0.43934C5.72064 0.158035 6.10218 0 6.5 0C6.89782 0 7.27936 0.158035 7.56066 0.43934C7.84196 0.720644 8 1.10218 8 1.5C8 1.89782 7.84196 2.27936 7.56066 2.56066C7.27936 2.84196 6.89782 3 6.5 3ZM6.5 8C6.10218 8 5.72064 7.84196 5.43934 7.56066C5.15804 7.27936 5 6.89782 5 6.5C5 6.10218 5.15804 5.72064 5.43934 5.43934C5.72064 5.15804 6.10218 5 6.5 5C6.89782 5 7.27936 5.15804 7.56066 5.43934C7.84196 5.72064 8 6.10218 8 6.5C8 6.89782 7.84196 7.27936 7.56066 7.56066C7.27936 7.84196 6.89782 8 6.5 8ZM6.5 13C6.10218 13 5.72064 12.842 5.43934 12.5607C5.15804 12.2794 5 11.8978 5 11.5C5 11.1022 5.15804 10.7206 5.43934 10.4393C5.72064 10.158 6.10218 10 6.5 10C6.89782 10 7.27936 10.158 7.56066 10.4393C7.84196 10.7206 8 11.1022 8 11.5C8 11.8978 7.84196 12.2794 7.56066 12.5607C7.27936 12.842 6.89782 13 6.5 13ZM6.5 18C6.10218 18 5.72064 17.842 5.43934 17.5607C5.15804 17.2794 5 16.8978 5 16.5C5 16.1022 5.15804 15.7206 5.43934 15.4393C5.72064 15.158 6.10218 15 6.5 15C6.89782 15 7.27936 15.158 7.56066 15.4393C7.84196 15.7206 8 16.1022 8 16.5C8 16.8978 7.84196 17.2794 7.56066 17.5607C7.27936 17.842 6.89782 18 6.5 18Z"
            fill="#94A3B8"
          />
        </svg>
      </div>
    );
  },
);
